import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  getDoc,
  serverTimestamp,
  getDocs,
  query,
  orderBy
} from 'firebase/firestore';

/**
 * Create a new user document in Firestore
 * @param {string} uid - Firebase Auth UID
 * @returns {Promise<void>}
 */
export const createUserDocument = async (uid) => {
  try {
    const userRef = doc(db, 'users', uid);
    await setDoc(userRef, {
      uid: uid,
      isAnonymous: true,
      createdAt: serverTimestamp(),
    }, { merge: true }); // merge: true prevents overwriting if doc already exists

    console.log('User document created:', uid);
  } catch (error) {
    console.error('Error creating user document:', error);
    throw error;
  }
};

/**
 * Create a new quiz session
 * @param {string} userId - Firebase Auth UID
 * @returns {Promise<string>} - Session ID
 */
export const createQuizSession = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const sessionId = `quiz_${userId}_${Date.now()}`;

    const sessionRef = doc(db, 'sessions', sessionId);
    await setDoc(sessionRef, {
      usersRef: [userRef],
      session_id: sessionId,
      metadata: {
        quiz_completed: false,
        quiz_type: 'personality',
        started_at: serverTimestamp(),
      },
      last_updated: serverTimestamp(),
    });

    console.log('Quiz session created:', sessionId);
    return sessionId;
  } catch (error) {
    console.error('Error creating quiz session:', error);
    throw error;
  }
};

/**
 * Generate a UUID v4
 * @returns {string} - UUID string
 */
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : ((r & 0x3) | 0x8);
    return v.toString(16);
  });
};

/**
 * Save a chat message (question or answer) to the session
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {Object} messageData - Chat message data
 * @param {string} messageData.type - 'ai' or 'user'
 * @param {string} messageData.message - Message text
 * @param {string} messageData.senderName - Display name (for user messages)
 * @param {Object} messageData.questionData - Optional question metadata
 * @returns {Promise<string>} - Chat document ID
 */
export const saveChatMessage = async (sessionId, userId, messageData) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);
    const userRef = doc(db, 'users', userId);
    const chatsCollectionRef = collection(sessionRef, 'chats');

    // Generate ISO 8601 timestamp string
    const createdAt = new Date().toISOString();
    const messageId = generateUUID();

    const chatData = {
      session_id: sessionId,
      sessionRef: sessionRef,
      userRef: userRef,
      chat: {
        type: messageData.type,
        message: messageData.message,
        createdAt: createdAt,
        messageId: messageId,
        ...(messageData.type === 'user' && {
          senderId: userId,
          senderName: messageData.senderName || '',
        }),
        ...(messageData.questionData && {
          question_id: messageData.questionData.id,
          question_order: messageData.questionData.order,
          question_type: messageData.questionData.type,
        }),
        ...(messageData.selectedOptions && {
          selected_options: messageData.selectedOptions,
        }),
      },
    };

    const chatDocRef = await addDoc(chatsCollectionRef, chatData);
    console.log('Chat message saved:', chatDocRef.id);
    return chatDocRef.id;
  } catch (error) {
    console.error('Error saving chat message:', error);
    throw error;
  }
};

/**
 * Mark quiz session as completed
 * @param {string} sessionId - Session ID
 * @returns {Promise<void>}
 */
export const completeQuizSession = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);
    await setDoc(sessionRef, {
      metadata: {
        quiz_completed: true,
        completed_at: serverTimestamp(),
      },
      last_updated: serverTimestamp(),
    }, { merge: true });

    console.log('Quiz session completed:', sessionId);
  } catch (error) {
    console.error('Error completing quiz session:', error);
    throw error;
  }
};

/**
 * Update user display name
 * @param {string} userId - User ID
 * @param {string} displayName - User's display name
 * @returns {Promise<void>}
 */
export const updateUserDisplayName = async (userId, displayName) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      display_name: displayName,
      displayNameCapturedAt: serverTimestamp(),
    }, { merge: true });

    console.log('User display name updated:', userId);
  } catch (error) {
    console.error('Error updating user display name:', error);
    throw error;
  }
};

/**
 * Update user email
 * @param {string} userId - User ID
 * @param {string} email - User's email
 * @returns {Promise<void>}
 */
export const updateUserEmail = async (userId, email) => {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, {
      email: email,
      emailCapturedAt: serverTimestamp(),
    }, { merge: true });

    console.log('User email updated:', userId);
  } catch (error) {
    console.error('Error updating user email:', error);
    throw error;
  }
};

/**
 * Get all chat messages for a session
 * @param {string} sessionId - Session ID
 * @returns {Promise<Array>} - Array of chat message objects
 */
export const getChatMessages = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);
    const chatsCollectionRef = collection(sessionRef, 'chats');
    const q = query(chatsCollectionRef, orderBy('chat.createdAt', 'asc'));

    const querySnapshot = await getDocs(q);
    const messages = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      // Flatten the chat structure
      messages.push({
        id: doc.id,
        type: data.chat.type,
        message: data.chat.message,
        createdAt: data.chat.createdAt,
        messageId: data.chat.messageId,
        senderId: data.chat.senderId,
        senderName: data.chat.senderName,
        questionData: {
          id: data.chat.question_id,
          order: data.chat.question_order,
          type: data.chat.question_type,
        },
        selectedOptions: data.chat.selected_options,
      });
    });

    console.log(`Fetched ${messages.length} chat messages from session:`, sessionId);
    return messages;
  } catch (error) {
    console.error('Error fetching chat messages:', error);
    throw error;
  }
};

/**
 * Get session metadata
 * @param {string} sessionId - Session ID
 * @returns {Promise<Object|null>} - Session data or null if not found
 */
export const getSessionMetadata = async (sessionId) => {
  try {
    const sessionRef = doc(db, 'sessions', sessionId);
    const sessionDoc = await getDoc(sessionRef);

    if (!sessionDoc.exists()) {
      console.log('Session not found:', sessionId);
      return null;
    }

    const data = sessionDoc.data();
    console.log('Session metadata fetched:', sessionId);
    return {
      sessionId: data.session_id,
      metadata: data.metadata || {},
      lastUpdated: data.last_updated,
    };
  } catch (error) {
    console.error('Error fetching session metadata:', error);
    throw error;
  }
};
